import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import API_BASE_URL from './config';
import ShareJobModal from './ShareJobModal';
import { JOB_STATUSES, NO_GO_REASONS, normalizeStatus } from './constants';

function JobDetails({ jobId, onBack, theme, toggleTheme }) {
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [scrapeHistory, setScrapeHistory] = useState([]);
    const [journalEntries, setJournalEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingTitle, setEditingTitle] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [editingDueDate, setEditingDueDate] = useState(false);
    const [newDueDate, setNewDueDate] = useState('');
    const [newJournalEntry, setNewJournalEntry] = useState('');
    const [userName, setUserName] = useState(localStorage.getItem('rfq_user_name') || 'User');

    // Single Status control (business-development/BD_METRICS_SPEC.md Section 6e). One canonical
    // choice atomically writes user_status + outcome + pursuit_stage on the backend.
    const [selectedStatus, setSelectedStatus] = useState('new');
    const [selectedNoGoReason, setSelectedNoGoReason] = useState('');
    const [statusSaved, setStatusSaved] = useState(false);
    const [statusError, setStatusError] = useState('');

    // Contacts state
    const [assignedContacts, setAssignedContacts] = useState([]);
    const [showAssignContactForm, setShowAssignContactForm] = useState(false);
    const [allContacts, setAllContacts] = useState([]);
    const [contactSettings, setContactSettings] = useState({});
    const [newAssignment, setNewAssignment] = useState({
        contact_id: '',
        capacity: '',
        status: 'Proposed',
        notes: ''
    });
    
    // Dev-only features state
    const [userRole, setUserRole] = useState(null);
    const [editingRfpNumber, setEditingRfpNumber] = useState(false);
    const [newRfpNumber, setNewRfpNumber] = useState('');
    const [rfpNumberError, setRfpNumberError] = useState('');
    
    // Share modal state
    const [showShareModal, setShowShareModal] = useState(false);

    useEffect(() => {
        loadJobDetails();
        loadAssignedContacts();
        loadAllContacts();
        loadContactSettings();
        checkUserRole();
    }, [jobId]);
    
    const checkUserRole = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/me`, {
                credentials: 'include'
            });
            const data = await response.json();
            setUserRole(data.role);
        } catch (error) {
            console.error('Error checking user role:', error);
        }
    };

    // Share functionality - opens modal with sharing options
    const handleShare = () => {
        setShowShareModal(true);
    };

    const loadJobDetails = () => {
        fetch(`${API_BASE_URL}/job_details/${jobId}`, {
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                setJob(data.job);
                setNewTitle(data.job.title);
                setNewDueDate(data.job.due_date || '');
                setNewRfpNumber(data.job.rfp_number || '');
                setSelectedStatus(normalizeStatus(data.job.user_status));
                setSelectedNoGoReason(data.job.no_go_reason || '');
                setScrapeHistory(data.scrape_history);
                setJournalEntries(data.journal_entries);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error loading job details:', err);
                setLoading(false);
            });
    };

    const updateTitle = () => {
        fetch(`${API_BASE_URL}/update_job_details`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ job_id: jobId, title: newTitle })
        })
            .then(res => res.json())
            .then(data => {
                setJob({ ...job, title: newTitle });
                setEditingTitle(false);
                console.log('Title updated:', data);
            })
            .catch(err => console.error('Error updating title:', err));
    };

    const updateDueDate = () => {
        fetch(`${API_BASE_URL}/update_job_details`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ job_id: jobId, due_date: newDueDate })
        })
            .then(res => res.json())
            .then(data => {
                setJob({ ...job, due_date: newDueDate });
                setEditingDueDate(false);
                console.log('Due date updated:', data);
            })
            .catch(err => console.error('Error updating due date:', err));
    };

    // Single Status control: one canonical slug atomically writes user_status + outcome +
    // pursuit_stage (+ no_go_reason / outcome_date / go_decision_date) on the backend.
    const updateStatus = (newStatus, noGoReason = null) => {
        fetch(`${API_BASE_URL}/update_job_status`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ job_id: jobId, status: newStatus, no_go_reason: noGoReason })
        })
            .then(res => res.json())
            .then(data => {
                if (data.error) {
                    setStatusError(data.error);
                    setStatusSaved(false);
                    return;
                }
                setJob({
                    ...job,
                    user_status: newStatus,
                    outcome: data.outcome,
                    pursuit_stage: data.pursuit_stage,
                    no_go_reason: data.no_go_reason,
                    outcome_date: data.outcome_date,
                    go_decision_date: data.go_decision_date,
                });
                setStatusError('');
                setStatusSaved(true);
                setTimeout(() => setStatusSaved(false), 2000);
                console.log('Status updated:', data);
            })
            .catch(err => {
                console.error('Error updating status:', err);
                setStatusSaved(false);
                setStatusError('Network error. Please try again.');
            });
    };

    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setSelectedStatus(newStatus);
        setStatusError('');

        if (newStatus === 'no_go') {
            // Reveal the reason dropdown and wait for the user to pick a reason before saving
            setSelectedNoGoReason(job.no_go_reason || '');
            return;
        }

        setSelectedNoGoReason('');
        updateStatus(newStatus, null);
    };

    const handleNoGoReasonChange = (e) => {
        const reason = e.target.value;
        setSelectedNoGoReason(reason);

        if (reason) {
            updateStatus('no_go', reason);
        }
    };

    const addJournalEntry = () => {
        if (!newJournalEntry.trim()) return;

        // Save user name to localStorage
        localStorage.setItem('rfq_user_name', userName);

        fetch(`${API_BASE_URL}/add_journal_entry`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                job_id: jobId,
                entry_text: newJournalEntry,
                user_name: userName
            })
        })
            .then(res => res.json())
            .then(data => {
                setJournalEntries([data.entry, ...journalEntries]);
                setNewJournalEntry('');
                console.log('Journal entry added:', data);
            })
            .catch(err => console.error('Error adding journal entry:', err));
    };
    
    // Dev-only: Edit RFP Number
    const updateRfpNumber = async () => {
        if (!newRfpNumber.trim()) {
            setRfpNumberError('RFP number cannot be empty');
            return;
        }
        
        setRfpNumberError('');
        
        try {
            // Check for duplicates first
            const response = await fetch(`${API_BASE_URL}/check_rfp_duplicate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    organization: job.organization,
                    rfp_number: newRfpNumber.trim(),
                    exclude_job_id: jobId
                })
            });
            
            const data = await response.json();
            
            if (data.is_duplicate) {
                setRfpNumberError(`❌ Duplicate! RFP# "${newRfpNumber}" already exists for ${job.organization}`);
                return;
            }
            
            // Update the RFP number
            const updateResponse = await fetch(`${API_BASE_URL}/update_job_details`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ 
                    job_id: jobId, 
                    rfp_number: newRfpNumber.trim() 
                })
            });
            
            const updateData = await updateResponse.json();
            
            if (updateResponse.ok) {
                setJob({ ...job, rfp_number: newRfpNumber.trim() });
                setEditingRfpNumber(false);
                setRfpNumberError('');
                alert('✅ RFP# updated successfully!');
            } else {
                setRfpNumberError(updateData.error || 'Failed to update RFP#');
            }
        } catch (err) {
            console.error('Error updating RFP#:', err);
            setRfpNumberError('Network error. Please try again.');
        }
    };
    
    // Dev-only: Delete Job
    const deleteJob = async () => {
        // Safety check 1: Only manual jobs
        if (job.added_by !== 'parsed' && job.added_by !== 'manual') {
            alert('❌ Can only delete manually added jobs.\n\nThis job was added by the scraper and should not be deleted.');
            return;
        }
        
        // Safety check 2: Check for user modifications
        const hasUserData = 
            (job.user_status && job.user_status !== 'new') ||
            (job.user_notes && job.user_notes.trim() !== '') ||
            journalEntries.length > 0 ||
            assignedContacts.length > 0;
        
        let warningMessage = `⚠️ Are you sure you want to DELETE this job?\n\n`;
        warningMessage += `RFP#: ${job.rfp_number}\n`;
        warningMessage += `Title: ${job.title}\n`;
        warningMessage += `City: ${job.organization}\n\n`;
        
        if (hasUserData) {
            warningMessage += `⚠️ WARNING: This job has user data:\n`;
            if (job.user_status && job.user_status !== 'new') {
                warningMessage += `• Status: ${job.user_status}\n`;
            }
            if (job.user_notes && job.user_notes.trim()) {
                warningMessage += `• Has notes\n`;
            }
            if (journalEntries.length > 0) {
                warningMessage += `• Has ${journalEntries.length} journal ${journalEntries.length === 1 ? 'entry' : 'entries'}\n`;
            }
            if (assignedContacts.length > 0) {
                warningMessage += `• Has ${assignedContacts.length} assigned ${assignedContacts.length === 1 ? 'contact' : 'contacts'}\n`;
            }
            warningMessage += `\n`;
        }
        
        warningMessage += `This action CANNOT be undone.\n\nType "DELETE" to confirm:`;
        
        const confirmation = prompt(warningMessage);
        
        if (confirmation !== 'DELETE') {
            alert('❌ Deletion cancelled');
            return;
        }
        
        try {
            const response = await fetch(`${API_BASE_URL}/delete_job/${jobId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            const data = await response.json();
            
            if (response.ok) {
                alert('✅ Job deleted successfully');
                // Navigate back to main page
                navigate('/');
            } else {
                alert(`❌ ${data.error || 'Failed to delete job'}`);
            }
        } catch (err) {
            console.error('Error deleting job:', err);
            alert('❌ Network error. Please try again.');
        }
    };

    const loadAssignedContacts = () => {
        fetch(`${API_BASE_URL}/job/${jobId}/contacts`, {
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                setAssignedContacts(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error('Error loading assigned contacts:', err));
    };

    const loadAllContacts = () => {
        fetch(`${API_BASE_URL}/contacts`, {
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                setAllContacts(Array.isArray(data) ? data : []);
            })
            .catch(err => console.error('Error loading contacts:', err));
    };

    const loadContactSettings = () => {
        fetch(`${API_BASE_URL}/contact_settings`, {
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                setContactSettings(data);
            })
            .catch(err => console.error('Error loading contact settings:', err));
    };

    const handleAssignContact = (e) => {
        e.preventDefault();
        
        if (!newAssignment.contact_id) {
            alert('Please select a contact');
            return;
        }

        fetch(`${API_BASE_URL}/contact_job_assignment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                contact_id: parseInt(newAssignment.contact_id),
                job_id: jobId,
                capacity: newAssignment.capacity,
                status: newAssignment.status,
                notes: newAssignment.notes
            })
        })
            .then(res => res.json())
            .then(data => {
                if (data.error) {
                    alert(data.error);
                } else {
                    setShowAssignContactForm(false);
                    setNewAssignment({
                        contact_id: '',
                        capacity: '',
                        status: 'Proposed',
                        notes: ''
                    });
                    loadAssignedContacts();
                }
            })
            .catch(err => {
                console.error('Error assigning contact:', err);
                alert('Failed to assign contact');
            });
    };

    const handleRemoveAssignment = (assignmentId) => {
        if (!window.confirm('Remove this contact assignment?')) {
            return;
        }

        fetch(`${API_BASE_URL}/contact_job_assignment/${assignmentId}`, {
            method: 'DELETE',
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                if (data.error) {
                    alert(data.error);
                } else {
                    loadAssignedContacts();
                }
            })
            .catch(err => {
                console.error('Error removing assignment:', err);
                alert('Failed to remove assignment');
            });
    };

    if (loading) {
        return <div className="container mt-5"><p>Loading job details...</p></div>;
    }

    if (!job) {
        return <div className="container mt-5"><p>Job not found.</p></div>;
    }

    return (
        <div className="container mt-4">
            {/* Back button, share button, and theme toggle */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex gap-2">
                    <button className="btn btn-secondary" onClick={onBack}>
                        ← Back to RFQs List
                    </button>
                    <button 
                        className="btn btn-primary" 
                        onClick={handleShare}
                        title="Share this job (mobile) or copy link (desktop)"
                    >
                        🔗 Share
                    </button>
                    <button 
                        className="btn btn-info" 
                        onClick={() => window.print()}
                        title="Print or save as PDF"
                    >
                        🖨️ Print/PDF
                    </button>
                    {userRole === 'dev' && job && (job.added_by === 'parsed' || job.added_by === 'manual') && (
                        <button 
                            className="btn btn-danger" 
                            onClick={deleteJob}
                            title="Delete this manually added job (Dev only)"
                        >
                            🗑️ Delete
                        </button>
                    )}
                </div>
                <button 
                    onClick={toggleTheme} 
                    className="theme-toggle"
                    title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                    aria-label="Toggle theme"
                >
                    {theme === 'light' ? '🌙' : '☀️'}
                </button>
            </div>

            {/* Job Header */}
            <div className="card mb-3">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-8">
                            <h5 className="card-title">
                                {editingTitle ? (
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newTitle}
                                            onChange={(e) => setNewTitle(e.target.value)}
                                        />
                                        <button className="btn btn-success" onClick={updateTitle}>
                                            Save
                                        </button>
                                        <button className="btn btn-secondary" onClick={() => {
                                            setEditingTitle(false);
                                            setNewTitle(job.title);
                                        }}>
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {job.title}
                                        <button
                                            className="btn btn-sm btn-outline-secondary ms-2"
                                            onClick={() => setEditingTitle(true)}
                                        >
                                            ✏️ Edit
                                        </button>
                                    </>
                                )}
                            </h5>
                            <p className="text-muted mb-1">
                                <strong>Organization:</strong>{' '}
                                <a 
                                    href={`/city/${encodeURIComponent(job.organization)}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(`/city/${encodeURIComponent(job.organization)}`);
                                    }}
                                    className="text-decoration-none"
                                    title={`View ${job.organization} profile`}
                                >
                                    {job.organization}
                                </a>
                            </p>
                            <p className="text-muted mb-1">
                                <strong>Job ID:</strong> <span className="font-monospace">{job.job_id}</span>
                            </p>
                            <p className="text-muted mb-1">
                                <strong>Org RFP #:</strong>{' '}
                                {editingRfpNumber ? (
                                    <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm d-inline-block"
                                            style={{ width: '200px', marginRight: '5px' }}
                                            value={newRfpNumber}
                                            onChange={(e) => setNewRfpNumber(e.target.value)}
                                        />
                                        <button className="btn btn-sm btn-success me-1" onClick={updateRfpNumber}>
                                            Save
                                        </button>
                                        <button className="btn btn-sm btn-secondary" onClick={() => {
                                            setEditingRfpNumber(false);
                                            setNewRfpNumber(job.rfp_number);
                                            setRfpNumberError('');
                                        }}>
                                            Cancel
                                        </button>
                                        {rfpNumberError && (
                                            <div className="text-danger small mt-1">{rfpNumberError}</div>
                                        )}
                                    </span>
                                ) : (
                                    <>
                                        {job.rfp_number}
                                        {userRole === 'dev' && (job.added_by === 'parsed' || job.added_by === 'manual') && (
                                            <button
                                                className="btn btn-sm btn-outline-secondary ms-2"
                                                onClick={() => setEditingRfpNumber(true)}
                                                title="Edit RFP# (Dev only, manual jobs only)"
                                            >
                                                ✏️ Edit
                                            </button>
                                        )}
                                    </>
                                )}
                            </p>
                        </div>
                        <div className="col-md-4 text-end">
                            <div className="mb-3">
                                <label className="form-label"><strong>Status:</strong></label>
                                <select
                                    className="form-select"
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                >
                                    {JOB_STATUSES.map(({ value, label, emoji }) => (
                                        <option key={value} value={value}>{emoji} {label}</option>
                                    ))}
                                    {job.user_status === 'completed' && (
                                        <option value="completed">Completed (legacy) — reassign to Won or Lost</option>
                                    )}
                                </select>
                                {selectedStatus === 'no_go' && (
                                    <select
                                        className="form-select mt-2"
                                        value={selectedNoGoReason}
                                        onChange={handleNoGoReasonChange}
                                        required
                                    >
                                        <option value="">-- Select No-Go reason (required) --</option>
                                        {NO_GO_REASONS.map(({ value, label }) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                )}
                                {statusSaved && (
                                    <div className="text-success small mt-1">Saved ✓</div>
                                )}
                                {statusError && (
                                    <div className="text-danger small mt-1">{statusError}</div>
                                )}
                            </div>
                            <p><strong>Work Type:</strong> <span className="badge bg-light text-dark">{job.work_type}</span></p>
                            <p>
                                <strong>Due Date:</strong> {editingDueDate ? (
                                    <span className="ms-2">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm d-inline-block"
                                            style={{width: '180px'}}
                                            value={newDueDate}
                                            onChange={(e) => setNewDueDate(e.target.value)}
                                            placeholder="MM/DD/YYYY HH:MM AM/PM"
                                        />
                                        <button className="btn btn-success btn-sm ms-1" onClick={updateDueDate}>
                                            Save
                                        </button>
                                        <button className="btn btn-secondary btn-sm ms-1" onClick={() => {
                                            setEditingDueDate(false);
                                            setNewDueDate(job.due_date);
                                        }}>
                                            Cancel
                                        </button>
                                    </span>
                                ) : (
                                    <span>
                                        {' '}{job.due_date}
                                        <button
                                            className="btn btn-sm btn-outline-secondary ms-2"
                                            onClick={() => setEditingDueDate(true)}
                                        >
                                            ✏️ Edit
                                        </button>
                                    </span>
                                )}
                            </p>
                            <p className="d-flex gap-2">
                                <a href={job.link} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                                    View RFQ →
                                </a>
                                <a 
                                    href={`${API_BASE_URL}/generate_calendar/${jobId}`} 
                                    className="btn btn-success btn-sm"
                                    title="Download .ics file for Outlook/Google Calendar"
                                    download
                                >
                                    📅 Add to Calendar
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrape History */}
            <div className="card mb-3">
                <div className="card-header">
                    <h6 className="mb-0">Scrape History</h6>
                </div>
                <div className="card-body">
                    <p className="text-muted mb-2">
                        <strong>First Seen:</strong> {new Date(job.first_seen).toLocaleString()}
                    </p>
                    <p className="text-muted mb-2">
                        <strong>Last Seen:</strong> {new Date(job.last_seen).toLocaleString()}
                    </p>
                    <p className="text-muted mb-2">
                        <strong>Added By:</strong> 
                        <span className={`badge ms-2 ${job.added_by === 'parsed' ? 'bg-info' : 'bg-success'}`}>
                            {job.added_by === 'parsed' ? '📝 Manual Parsing' : '🤖 Scraper'}
                        </span>
                    </p>
                    {scrapeHistory.length > 0 && (
                        <div className="mt-3">
                            <small className="text-muted">This job has been scraped {scrapeHistory.length} time(s)</small>
                        </div>
                    )}
                </div>
            </div>

            {/* Assigned Contacts */}
            <div className="card mb-3">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">👥 Assigned Contacts ({assignedContacts.length})</h6>
                        <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => setShowAssignContactForm(!showAssignContactForm)}
                        >
                            {showAssignContactForm ? '✖ Cancel' : '+ Assign Contact'}
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {showAssignContactForm && (
                        <div className="card bg-light mb-3">
                            <div className="card-body">
                                <form onSubmit={handleAssignContact}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Contact *</label>
                                            <select
                                                className="form-select"
                                                value={newAssignment.contact_id}
                                                onChange={(e) => setNewAssignment({...newAssignment, contact_id: e.target.value})}
                                                required
                                            >
                                                <option value="">Select contact...</option>
                                                {allContacts.map(contact => (
                                                    <option key={contact.id} value={contact.id}>
                                                        {contact.name} {contact.company_name && `(${contact.company_name})`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Capacity/Role</label>
                                            <select
                                                className="form-select"
                                                value={newAssignment.capacity}
                                                onChange={(e) => setNewAssignment({...newAssignment, capacity: e.target.value})}
                                            >
                                                <option value="">Select role...</option>
                                                {(contactSettings.capacity_options || []).map((option, idx) => (
                                                    <option key={idx} value={option}>{option}</option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={newAssignment.status}
                                                onChange={(e) => setNewAssignment({...newAssignment, status: e.target.value})}
                                            >
                                                {(contactSettings.assignment_statuses || ['Proposed', 'Confirmed', 'Won', 'Lost']).map((status, idx) => (
                                                    <option key={idx} value={status}>{status}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">Notes</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={newAssignment.notes}
                                            onChange={(e) => setNewAssignment({...newAssignment, notes: e.target.value})}
                                            placeholder="e.g., 'Reached out via email' or 'Partnering as GC'"
                                        />
                                    </div>
                                    
                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-primary">Assign Contact</button>
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary"
                                            onClick={() => setShowAssignContactForm(false)}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                    
                    {assignedContacts.length === 0 ? (
                        <p className="text-muted">No contacts assigned yet.</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Contact</th>
                                        <th>Company</th>
                                        <th>Capacity</th>
                                        <th>Status</th>
                                        <th>Notes</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignedContacts.map((assignment) => (
                                        <tr key={assignment.assignment_id}>
                                            <td>
                                                <strong>{assignment.name}</strong>
                                                {assignment.phone && <div className="small text-muted">{assignment.phone}</div>}
                                                {assignment.email && (
                                                    <div className="small">
                                                        <a href={`mailto:${assignment.email}`}>{assignment.email}</a>
                                                    </div>
                                                )}
                                            </td>
                                            <td>{assignment.company_name || '-'}</td>
                                            <td>
                                                {assignment.capacity && (
                                                    <span className="badge bg-info">{assignment.capacity}</span>
                                                )}
                                            </td>
                                            <td>
                                                {assignment.status && (
                                                    <span className={`badge bg-${
                                                        assignment.status === 'Won' ? 'success' :
                                                        assignment.status === 'Confirmed' ? 'info' :
                                                        assignment.status === 'Lost' ? 'danger' : 'warning'
                                                    }`}>
                                                        {assignment.status}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="small">{assignment.notes || '-'}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleRemoveAssignment(assignment.assignment_id)}
                                                    title="Remove assignment"
                                                >
                                                    ✖
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Job Info - Extra details from parsing */}
            {job.job_info && (
                <div className="card mb-3">
                    <div className="card-header">
                        <h6 className="mb-0">Additional Job Information</h6>
                    </div>
                    <div className="card-body">
                        <pre className="mb-0" style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}>
                            {job.job_info}
                        </pre>
                    </div>
                </div>
            )}

            {/* Journal Entries */}
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Project Journal</h6>
                </div>
                <div className="card-body">
                    {/* Add new entry */}
                    <div className="mb-4">
                        <label className="form-label"><strong>Add Journal Entry</strong></label>
                        <div className="row mb-2">
                            <div className="col-md-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Your name"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                />
                            </div>
                        </div>
                        <textarea
                            className="form-control mb-2"
                            rows="3"
                            placeholder="e.g., 'Submitted to BethAnn for proposal generation' or 'Called XYZ Construction for partnering opportunity'"
                            value={newJournalEntry}
                            onChange={(e) => setNewJournalEntry(e.target.value)}
                        ></textarea>
                        <button
                            className="btn btn-primary"
                            onClick={addJournalEntry}
                            disabled={!newJournalEntry.trim()}
                        >
                            Add Entry
                        </button>
                    </div>

                    {/* Display entries */}
                    {journalEntries.length === 0 ? (
                        <p className="text-muted">No journal entries yet. Start tracking your progress!</p>
                    ) : (
                        <div>
                            {journalEntries.map((entry, index) => (
                                <div key={index}>
                                    <div className="p-3 bg-light rounded mb-2">
                                        <div className="d-flex justify-content-between mb-2">
                                            <strong>{entry.user_name}</strong>
                                            <small className="text-muted">
                                                {new Date(entry.created_at).toLocaleString()}
                                            </small>
                                        </div>
                                        <p className="mb-0">{entry.text}</p>
                                    </div>
                                    {index < journalEntries.length - 1 && <hr style={{margin: '0.5rem 0'}} />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Share Job Modal */}
            {showShareModal && job && (
                <ShareJobModal 
                    job={job} 
                    onClose={() => setShowShareModal(false)} 
                />
            )}
        </div>
    );
}

export default JobDetails;

